/**
 * Wire DTOs shared by the API (producer) and the web app (consumer).
 *
 * Field names deliberately mirror the reference project's api.schemas.ts where
 * the inbox UI is reused (ConversationSummaryDto, ConversationThreadDto,
 * ChatMessageDto, TemplateDto) so the copied adapters/components keep working.
 * Dates are ISO-8601 strings except waTimestamp/deliveryTime/readTime which are
 * Unix seconds (reference convention).
 */

// ---------------------------------------------------------------------------
// Paging (reference/ABP shape: SkipCount/MaxResultCount -> { items, totalCount })
// ---------------------------------------------------------------------------

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

export interface PagedQuery {
  SkipCount?: number;
  MaxResultCount?: number;
  Sorting?: string;
}

// ---------------------------------------------------------------------------
// Session / driver
// ---------------------------------------------------------------------------

export interface DriverDto {
  id: string;
  name: string;
  /**
   * E.164 without leading +. Login identifier via Bawabah — at least one of
   * phone/email is always present.
   */
  phone: string | null;
  email: string | null;
  /** Company/shipping brand shown to clients — optional, set in onboarding/settings. */
  company: string | null;
  /**
   * The number the driver actually calls clients from (may differ from the
   * login phone). E.164 digits without +. Optional.
   */
  callingPhone: string | null;
  language: "ar" | "en";
  creditsBalance: number;
  /**
   * ISO date the current balance stops being usable, or null when nothing was
   * purchased yet. Each purchase resets it to now + the plan's expiryDays.
   */
  creditsExpiresAt: string | null;
}

export interface ChannelDto {
  id: string;
  name: string;
  /** Display phone of the WhatsApp business number. */
  displayPhone: string;
}

export interface SessionDto {
  userId: string;
  userName: string;
  language: "ar" | "en";
  currency: string;
  country: string;
  permissions: string[];
  creditsBalance: number;
  /**
   * DISABLE_APP_PAYMENTS on the API: the NATIVE APP hides purchase surfaces
   * and shows a "manage your credits on the website" note instead. The web
   * client ignores this flag entirely.
   */
  appPaymentsDisabled: boolean;
  driver: DriverDto;
  channel: ChannelDto | null;
}

export interface AuthResultDto {
  accessToken: string;
  driver: DriverDto;
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

/**
 * A location a client shared in-chat, remembered on the client record. A new
 * location is only saved when it is more than SAVED_LOCATION_MIN_DISTANCE_M
 * away from every one already saved (the same doorstep shared twice is one
 * entry).
 */
export interface SavedLocation extends LocationPayload {
  /** ISO date the location was first saved. */
  savedAt: string;
}

/** Minimum distance between two saved locations of the same client. */
export const SAVED_LOCATION_MIN_DISTANCE_M = 150;

export interface ClientDto {
  id: string;
  name: string;
  /** E.164 without leading +. */
  phone: string;
  /**
   * Gathered automatically, never asked for: the nearest known city to a
   * location the client shared, or the city read off a label / import row.
   */
  city: string | null;
  /** Detected from the script of the client's first text reply; null until then. */
  language: "ar" | "en" | null;
  savedLocations: SavedLocation[];
  creationTime: string;
}

// ---------------------------------------------------------------------------
// Inbox
// ---------------------------------------------------------------------------

export type MessageDirection = "Inbound" | "Outbound";
export type MessageDeliveryStatus =
  | "Pending"
  | "Sent"
  | "Delivered"
  | "Read"
  | "Failed";
export type MessageType =
  | "Text"
  | "Image"
  | "Video"
  | "Audio"
  | "Document"
  | "Sticker"
  | "Template"
  | "Location"
  | "Contacts"
  | "Unknown";

/** Lifecycle of the delivery a conversation represents. */
export type DeliveryStatus = "New" | "Ongoing" | "Delivered" | "Cancelled";

/**
 * How a delivery was added: typed by hand, from a spreadsheet / contacts list,
 * read off a photographed shipping label, or from a WhatsApp link opened with
 * the app (wa.me / whatsapp://).
 */
export type ShipmentSource = "Manual" | "List" | "Label" | "OpenWithApp";

/** Cash on delivery, prepaid, or something else the label / file said. */
export type ShipmentPaymentType = "COD" | "Paid" | "Other";

export type SenderType = "Company" | "Individual";

/**
 * Optional shipment facts gathered automatically (label OCR, import columns)
 * and stored on the delivery. The driver is never asked to fill these in —
 * whatever a source did not provide stays null.
 */
export interface ShipmentDetailsDto {
  source: ShipmentSource;
  paymentType: ShipmentPaymentType | null;
  /** Price of the goods, in halalas (1 SAR = 100). */
  costHalalas: number | null;
  /** Shipping fee, in halalas. */
  shippingCostHalalas: number | null;
  /** The merchant / person who sent the parcel (not the carrier). */
  senderName: string | null;
  senderType: SenderType | null;
  senderWebsite: string | null;
  weightGrams: number | null;
  /**
   * Saudi National Address short code: 4 letters + 4 digits (e.g. RRRD2929),
   * stored upper-case without spaces.
   */
  nationalAddress: string | null;
}

/**
 * A conversation IS a delivery (شحنة): its id doubles as the delivery id, and
 * it carries the shipment fields (tracking number, status, and the per-delivery
 * company/driver info snapshotted from the driver profile at creation).
 */
export interface ConversationSummaryDto extends ShipmentDetailsDto {
  id: string;
  clientId: string;
  name: string;
  /** E.164 without leading +. */
  phoneNumber: string;
  trackingNumber: string | null;
  deliveryStatus: DeliveryStatus;
  /** Per-delivery override; defaults from the driver profile at creation. */
  company: string | null;
  driverName: string | null;
  driverCallingPhone: string | null;
  preview: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  /** ISO date; null/past means the 24h window is closed. */
  windowExpiresAt: string | null;
  isStarred: boolean;
  lastMessageDirection: MessageDirection | null;
  lastMessageDelivery: MessageDeliveryStatus | null;
  /**
   * Last location the client shared in-chat, or null. Receiving one also
   * auto-advances the delivery New -> Ongoing.
   */
  clientLocation: LocationPayload | null;
  /**
   * A finished (Delivered/Cancelled) delivery whose chat a client message
   * reopened — derived, never stored. The driver can reply, or close the
   * chat again to free the number.
   */
  reopened: boolean;
}

/**
 * Fields Gemini reads off a photographed shipping label; null = not found.
 * The first four drive the delivery form; the rest are optional extras stored
 * on the delivery / client silently whenever the label carried them.
 */
export interface ShippingLabelDataDto {
  name: string | null;
  phone: string | null;
  trackingNumber: string | null;
  carrier: string | null;
  paymentType: ShipmentPaymentType | null;
  costHalalas: number | null;
  shippingCostHalalas: number | null;
  senderName: string | null;
  senderType: SenderType | null;
  senderWebsite: string | null;
  weightGrams: number | null;
  nationalAddress: string | null;
  /** Recipient city as printed on the label. */
  city: string | null;
}

export interface UploadMediaResultDto {
  /** Meta media id — pass back in SendMessageInput.mediaId. */
  mediaId: string;
  mimeType: string;
  fileName: string;
}

/** Parsed shape of ChatMessageDto.body for type "Location" (JSON string). */
export interface LocationPayload {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
}

export interface ChatMessageDto {
  id: string;
  conversationId: string;
  wamid: string | null;
  direction: MessageDirection;
  type: MessageType;
  /** Text body / media caption; for type "Location" a JSON LocationPayload. */
  body: string | null;
  /**
   * For media messages: the app's own proxy path
   * (`/api/inbox/media/{messageId}`) — append the access token as `?token=`
   * when using it in an <img>/<video>/<audio> src (no Authorization header there).
   */
  mediaUrl: string | null;
  mediaMimeType: string | null;
  mediaFileName: string | null;
  templateName: string | null;
  /** Unix seconds. */
  waTimestamp: number | null;
  /** Unix seconds. */
  deliveryTime: number | null;
  /** Unix seconds. */
  readTime: number | null;
  creationTime: string;
  delivery: MessageDeliveryStatus;
  errorCode: string | null;
  replyToWamid: string | null;
  /** The driver's own reaction emoji, or null. One per side (WhatsApp rule). */
  driverReaction: string | null;
  /** The client's reaction emoji, or null. */
  clientReaction: string | null;
}

export interface ConversationThreadDto extends ConversationSummaryDto {
  windowOpen: boolean;
  /**
   * Free-messages offer: driver messages this conversation can still send
   * at no cost, or null while no offer is running.
   */
  freeMessagesLeft: number | null;
  /** Newest page of messages, oldest-first. */
  messages: ChatMessageDto[];
  totalMessageCount: number;
}

export interface InboxCountsDto {
  open: number;
  closed: number;
  unread: number;
  /** Deliveries still on the road: status New + Ongoing. */
  undelivered: number;
  delivered: number;
  /**
   * Conversations with unread messages per list filter (non-archived) — the
   * chips show these so a reply landing in a delivered/cancelled chat is
   * visible from any view.
   */
  unreadUndelivered: number;
  unreadDelivered: number;
  unreadCancelled: number;
}

// ---------------------------------------------------------------------------
// Templates (Meta message templates; utility-only surface)
// ---------------------------------------------------------------------------

export type TemplateCategory = "Utility" | "Authentication" | "Marketing";
export type TemplateStatus = "Approved" | "Pending" | "Rejected" | "Unknown";

export interface TemplateDto {
  id: string;
  channelId: string;
  /** Meta template id. */
  metaId: string;
  name: string;
  language: string;
  category: TemplateCategory;
  status: TemplateStatus;
  /** Body with {{1}}-style placeholders. */
  bodyText: string;
  /** Ordered placeholder names, e.g. ["1","2"]. */
  variables: string[];
  hasMediaHeader: boolean;
}

// ---------------------------------------------------------------------------
// Billing / credits
// ---------------------------------------------------------------------------

export interface PlanDto {
  id: string;
  nameEn: string;
  nameAr: string;
  credits: number;
  /** Marketing bonus ("+100 free") granted on top of `credits` at purchase. */
  bonusCredits: number;
  /** Price in halalas (1 SAR = 100 halalas). */
  priceHalalas: number;
  currency: string;
  /** How many days the purchased credits stay valid. */
  expiryDays: number;
}

export type PaymentStatus = "Pending" | "Paid" | "Failed" | "Expired";

export interface PaymentDto {
  id: string;
  planId: string;
  planNameEn: string;
  planNameAr: string;
  credits: number;
  amountHalalas: number;
  currency: string;
  status: PaymentStatus;
  creditedAt: string | null;
  creationTime: string;
}

export interface CheckoutResultDto {
  paymentId: string;
  checkoutUrl: string;
}

export type CreditTransactionReason =
  | "template_send"
  | "chat_send"
  | "plan_purchase"
  | "refund"
  | "admin_adjust"
  /** Complimentary credits every new account starts with (WELCOME_CREDITS). */
  | "welcome_bonus"
  /** Free credits an admin handed to one driver from the dashboard. */
  | "gift"
  /** Write-off of a balance whose validity window ended — expired is gone. */
  | "expired";

export interface CreditTransactionDto {
  id: string;
  /** Signed: negative = debit, positive = credit. */
  amount: number;
  reason: CreditTransactionReason;
  balanceAfter: number;
  creationTime: string;
}

// ---------------------------------------------------------------------------
// Realtime
// ---------------------------------------------------------------------------

export type InboxChangedReason = "message" | "status" | "reaction";

/**
 * Enough of the new message to raise a local notification without a refetch —
 * the client is usually backgrounded when this matters, so a round trip would
 * be both slow and likely to fail.
 */
export interface InboxChangedPreview {
  clientName: string;
  /** Same one-line preview stored on the conversation (media types → "[Image]" etc). */
  text: string;
  direction: MessageDirection;
}

export interface InboxChangedEvent {
  channelId: string;
  conversationId: string | null;
  reason: InboxChangedReason | null;
  /** Only attached to inbound `reason: "message"` events. */
  preview?: InboxChangedPreview | null;
}

/** socket.io event name for inbox change notifications. */
export const INBOX_CHANGED_EVENT = "inboxChanged";

/** Flat cost of sending one utility template (v1: constant, no config table). */
export const TEMPLATE_SEND_COST = 1;

/**
 * Flat cost of one free-form chat message (text / media / location inside the
 * 24h window). Inbound client messages, reads and reactions stay free.
 */
export const CHAT_MESSAGE_COST = 1;

/**
 * Complimentary credits granted once to every new driver on sign-up (and
 * backfilled to existing drivers who never opened a conversation). No expiry.
 */
export const WELCOME_CREDITS = 100;

// ---------------------------------------------------------------------------
// Delivery import (CSV / Excel)
// ---------------------------------------------------------------------------

/**
 * Delivery fields a spreadsheet column can be matched to. The web app builds
 * its column-matching UI from this list (labels are localized client-side);
 * the API validates the same keys through `importDeliveryRowSchema`.
 * Template variables are matched separately — they are per-template.
 */
export const DELIVERY_IMPORT_FIELDS = [
  { key: "clientPhone", required: true, extra: false },
  { key: "clientName", required: false, extra: false },
  { key: "trackingNumber", required: false, extra: false },
  { key: "company", required: false, extra: false },
  { key: "driverName", required: false, extra: false },
  { key: "driverCallingPhone", required: false, extra: false },
  // `extra` columns are shipment facts stored silently when the file has
  // them; the matching UI tucks them away so they never read as a prompt.
  { key: "paymentType", required: false, extra: true },
  { key: "cost", required: false, extra: true },
  { key: "shippingCost", required: false, extra: true },
  { key: "senderName", required: false, extra: true },
  { key: "senderWebsite", required: false, extra: true },
  { key: "weight", required: false, extra: true },
  { key: "nationalAddress", required: false, extra: true },
  { key: "clientCity", required: false, extra: true },
] as const;

export type DeliveryImportField =
  (typeof DELIVERY_IMPORT_FIELDS)[number]["key"];

/**
 * `Skipped` rows were never attempted — the import stops sending as soon as the
 * driver runs out of credits, so the remainder is reported rather than failed.
 */
export type DeliveryImportRowStatus = "Created" | "Failed" | "Skipped";

export interface DeliveryImportRowResultDto {
  /** 1-based row number in the source file, for error reporting. */
  rowNumber: number;
  /** E.164 without leading +. */
  clientPhone: string;
  clientName: string | null;
  status: DeliveryImportRowStatus;
  /** The created delivery (= conversation) id, when status is `Created`. */
  conversationId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface ImportDeliveriesResultDto {
  created: number;
  failed: number;
  skipped: number;
  /** One entry per submitted row, in submission order. */
  results: DeliveryImportRowResultDto[];
}
